import { Link } from "react-router-dom";
import { useAuth } from "@/App";
import { 
  Scissors, 
  Mail, 
  Phone, 
  MapPin,
  Facebook,
  Twitter,
  Instagram,
  Youtube,
  Heart,
  Trophy,
  Users,
  Calendar,
  Gift,
  Shield,
  HelpCircle,
  FileText,
  Briefcase,
  Star,
  Settings,
  Play,
  BookOpen,
  Code,
  Video
} from "lucide-react";

const SUPER_ADMIN_EMAIL = "georgebroughton71@gmail.com";

const AppFooter = () => {
  const { user } = useAuth();
  // For now, only show admin link to super admin - others need to be added via the admin panel
  const isAdmin = user?.email?.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase();
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    forClients: [
      { label: "Find Stylists", href: "/find-stylists", icon: Users },
      { label: "Book Appointment", href: "/find-stylists", icon: Calendar },
      { label: "Loyalty Rewards", href: "/register?role=client", icon: Gift },
      { label: "Client Dashboard", href: "/dashboard", icon: Star }
    ],
    forProfessionals: [
      { label: "Join as Stylist", href: "/register?role=stylist", icon: Scissors },
      { label: "Join as Owner", href: "/register?role=owner", icon: Briefcase },
      { label: "Affiliate Program", href: "/soldiers", icon: Shield },
      { label: "Soldier Program Guide", href: "/docs/soldier-program-guide.html", icon: FileText, external: true },
      { label: "Referral Program", href: "/referral-program", icon: Trophy },
      { label: "Pricing Plans", href: "/membership", icon: Gift },
      { label: "Stylist Dashboard", href: "/stylist", icon: Calendar }
    ],
    company: [
      { label: "About Us", href: "/about", icon: Heart },
      { label: "For Investors", href: "/investors", icon: Briefcase },
      { label: "Invite Stylists", href: "/invite-stylist", icon: Users },
      { label: "Contact Us", href: "/contact", icon: Mail }
    ],
    support: [
      { label: "Help Center", href: "/faq", icon: HelpCircle },
      { label: "How It Works", href: "/#how-it-works", icon: Play },
      { label: "User Guide", href: "/user-guide", icon: BookOpen },
      { label: "Video Tutorials", href: "/user-guide", icon: Video },
      { label: "Getting Started", href: "/onboarding-guide", icon: Star },
      { label: "FAQ", href: "/faq", icon: HelpCircle },
      { label: "Privacy Policy", href: "#", icon: Shield },
      { label: "Terms of Service", href: "#", icon: FileText },
      // Developer Portal - only visible to Super Admin
      ...(isAdmin ? [{ label: "Developer Portal", href: "/developer", icon: Code }] : [])
    ],
    admin: isAdmin ? [
      { label: "Admin Dashboard", href: "/admin", icon: Settings }
    ] : []
  };

  const socialLinks = [
    { icon: Instagram, href: "https://instagram.com/myhairchairhq", label: "Follow us @myhairchairhq" },
    { icon: Facebook, href: "https://facebook.com/myhairchair", label: "Facebook" },
    { icon: Twitter, href: "https://twitter.com/myhairchair", label: "Twitter" },
    { icon: Youtube, href: "https://youtube.com/myhairchair", label: "YouTube" }
  ];

  return (
    <footer className="bg-stone-950 border-t border-stone-800">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-4 lg:col-span-1 mb-6 lg:mb-0">
            <Link to="/" className="flex items-center mb-4">
              <img 
                src="/images/myhairchair-logo.png" 
                alt="MyHairChair" 
                className="h-12 sm:h-14 w-auto"
              />
            </Link>
            <p className="text-stone-400 text-sm mb-2">
              Your Chair. Your Rules. Your Rewards.
            </p>
            <p className="text-amber-400/80 text-xs mb-4 font-medium">
              Built by Barbers and Stylists, for Barbers and Stylists
            </p>
            
            {/* Social Links */}
            <div className="mb-4">
              <a 
                href="https://instagram.com/myhairchairhq"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-pink-400 hover:text-pink-300 text-sm font-medium transition-colors"
              >
                <Instagram className="w-4 h-4" />
                Follow us @myhairchairhq
              </a>
            </div>
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-stone-800 hover:bg-stone-700 rounded-lg flex items-center justify-center text-stone-400 hover:text-white transition-colors"
                  aria-label={social.label}
                  title={social.label}
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* For Clients */}
          <div>
            <h4 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">For Clients</h4>
            <ul className="space-y-3">
              {footerLinks.forClients.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.href}
                    className="text-stone-400 hover:text-amber-400 text-sm flex items-center gap-2 transition-colors"
                  >
                    <link.icon className="w-4 h-4" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* For Professionals */}
          <div>
            <h4 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">For Professionals</h4>
            <ul className="space-y-3">
              {footerLinks.forProfessionals.map((link) => (
                <li key={link.label}>
                  {link.external ? (
                    <a 
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-stone-400 hover:text-amber-400 text-sm flex items-center gap-2 transition-colors"
                    >
                      <link.icon className="w-4 h-4" />
                      {link.label}
                    </a>
                  ) : (
                    <Link 
                      to={link.href}
                      className="text-stone-400 hover:text-amber-400 text-sm flex items-center gap-2 transition-colors"
                    >
                      <link.icon className="w-4 h-4" />
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  {link.href.startsWith("mailto") ? (
                    <a 
                      href={link.href}
                      className="text-stone-400 hover:text-amber-400 text-sm flex items-center gap-2 transition-colors"
                    >
                      <link.icon className="w-4 h-4" />
                      {link.label}
                    </a>
                  ) : (
                    <Link 
                      to={link.href}
                      className="text-stone-400 hover:text-amber-400 text-sm flex items-center gap-2 transition-colors"
                    >
                      <link.icon className="w-4 h-4" />
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">Support</h4>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.href}
                    className="text-stone-400 hover:text-amber-400 text-sm flex items-center gap-2 transition-colors"
                  >
                    <link.icon className="w-4 h-4" />
                    {link.label}
                  </Link>
                </li>
              ))}
              {/* Admin Link - Only visible to admins */}
              {isAdmin && footerLinks.admin.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.href}
                    className="text-red-400 hover:text-red-300 text-sm flex items-center gap-2 transition-colors"
                  >
                    <link.icon className="w-4 h-4" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Features Highlight */}
        <div className="mt-12 pt-8 border-t border-stone-800">
          <h4 className="text-white font-bold mb-6 text-center">Platform Features</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[
              { icon: Calendar, label: "Easy Booking" },
              { icon: Gift, label: "Loyalty Rewards" },
              { icon: Trophy, label: "Referral Program" },
              { icon: Shield, label: "Secure Payments" },
              { icon: Users, label: "Client Management" },
              { icon: Star, label: "Reviews & Ratings" }
            ].map((feature) => (
              <div 
                key={feature.label}
                className="flex items-center gap-2 bg-stone-900 rounded-lg px-3 py-2 text-center justify-center"
              >
                <feature.icon className="w-4 h-4 text-amber-400" />
                <span className="text-stone-300 text-xs">{feature.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-stone-800 py-6">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-stone-500 text-sm">
            © {currentYear} MyHairChair. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="#" className="text-stone-500 hover:text-stone-300 text-sm">
              Privacy Policy
            </Link>
            <Link to="#" className="text-stone-500 hover:text-stone-300 text-sm">
              Terms of Service
            </Link>
            <Link to="#" className="text-stone-500 hover:text-stone-300 text-sm">
              Cookies
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default AppFooter;
